import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

/**
 * Fades content in the first time it enters the viewport.
 * Anything already on screen at mount is shown right away, so content never
 * depends on an observer callback that may be delayed (hidden tab, prerender).
 * `delay` staggers siblings without extra CSS classes.
 */
export default function Reveal({ children, delay = 0, as: Tag = 'div', className = '' }) {
  const { ref, inView } = useInView({
    threshold: 0.15,
    triggerOnce: true,
    rootMargin: '0px 0px -8% 0px',
    fallbackInView: true,
  });

  const nodeRef = useRef(null);
  const [visibleOnMount, setVisibleOnMount] = useState(false);

  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return;

    const { top } = node.getBoundingClientRect();
    if (top < window.innerHeight * 0.92) setVisibleOnMount(true);
  }, []);

  const setRefs = (node) => {
    nodeRef.current = node;
    ref(node);
  };

  const isVisible = inView || visibleOnMount;

  return (
    <Tag
      ref={setRefs}
      className={`reveal ${isVisible ? 'is-visible' : ''} ${className}`.trim()}
      style={{ '--reveal-delay': `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}
